import { NavigateFunction } from "react-router-dom";
import { DefectDataItem } from "../types/DefectsDataItem";
import { SharedDefectItem } from "../types/SharedDefectItem";
import { DefectStatus } from "../types/DefectStatus";
import { searchAssets } from "../services/AssetsService";
import { getTranslationObject } from "../services/Translation";
import {
    DefectRef,
    IdentifiableDefect,
    defectKey,
    findDefect
} from "./DefectIdentity";

const t = getTranslationObject();

/**
 * Query string parameter carrying the inspection guid on rectification routes.
 * Defect ids are only unique within an inspection, so the id in the path is not
 * enough on its own to identify a defect.
 */
export const INSPECTION_ID_PARAM = "inspectionId";

/** Builds a rectification route that identifies the defect unambiguously. */
export const buildRectificationUrl = (
    prefix: string,
    defect: IdentifiableDefect
) =>
    `/${prefix}/${defect.defectId}/rectification?${INSPECTION_ID_PARAM}=${encodeURIComponent(defect.inspectionId)}`;

export const navigateToRectificationView = <T extends IdentifiableDefect>(defects: T[] | undefined, defect: DefectRef, navigate: NavigateFunction, prefix: string) => {
    const defectToNavigate = findDefect(defects, defect);

    if (defectToNavigate) {
        navigate(buildRectificationUrl(prefix, defectToNavigate));
    }
}

export const getRectificationUrl = <T extends IdentifiableDefect>(defects: T[] | undefined, defect: DefectRef, prefix: string) => {
    const defectToNavigate = findDefect(defects, defect);

    if (defectToNavigate) {
        return buildRectificationUrl(prefix, defectToNavigate);
    }

    return "";
}

export const navigateSharedDefectRectificationView = <T extends IdentifiableDefect>(sharedDefects: T[] | undefined, defect: DefectRef, navigate: NavigateFunction, prefix: string) => {
    const defectToNavigate = findDefect(sharedDefects, defect);

    if (defectToNavigate) {
        navigate(buildRectificationUrl(prefix, defectToNavigate));
    }
}

export const getDefectEvidenceValue = (hasEvidence: boolean) =>
    t(hasEvidence ? "defectsTable.provided" : "defectsTable.notProvided");

export const mapRectifiedEvidence = async (
    items: (DefectDataItem | SharedDefectItem)[]
) => {
    const rectifiedDefects = items.filter(
        (item) => item.status === DefectStatus.rectified
    );

    if (!rectifiedDefects.length) {
        return items;
    }

    const results = await Promise.all(
        rectifiedDefects.map((item) => {
            const imo = item.imo ?? (item as DefectDataItem).vessel.imo;
            return searchAssets(`imo=${imo}&defect-id=${item.defectId}`);
        })
    );

    const evidenceByDefect = new Map(
        rectifiedDefects.map((defect, index) => [
            defectKey(defect),
            getDefectEvidenceValue(!!results[index]?.length),
        ])
    );

    return items.map((item) => {
        const evidence = evidenceByDefect.get(defectKey(item));

        if (evidence !== undefined) {
            return { ...item, evidence };
        }
        return item;
    });
};
